import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLang } from '../contexts/LanguageContext';
import { t } from '../utils/translations';
import api from '../utils/api';
import Layout from '../components/Layout';
import StatCards from '../components/StatCards';
import { StatusBadge, CategoryBadge, HashtagChip } from '../components/Badges';
import { Plus, ArrowRight, BookOpen, Hash } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function HomePage() {
  const { user } = useAuth();
  const { lang } = useLang();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentTickets, setRecentTickets] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, ticketsRes] = await Promise.all([
          api.get('/tickets/stats'),
          api.get('/tickets', { params: { my_tickets: true } })
        ]);
        setStats(statsRes.data);
        setRecentTickets(ticketsRes.data.slice(0, 5));
      } catch {}
    };
    fetchData();
  }, [user?.role]);

  return (
    <Layout>
      <div className="animate-fade-in">
        {/* Welcome */}
        <div className="mb-6">
          <h1 data-testid="welcome-title" className="text-2xl font-bold text-white mb-1">
            {lang === 'fr' ? `Bonjour, ${user?.name}` : `Hello, ${user?.name}`}
          </h1>
          <p className="text-sm" style={{ color: '#9CA3AF' }}>
            {lang === 'fr' ? 'Bienvenue sur APOLLO Ticketing' : 'Welcome to APOLLO Ticketing'}
          </p>
        </div>

        {/* Stats */}
        {stats && <StatCards stats={stats} />}

        {/* Quick Actions */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
          <button data-testid="quick-create" onClick={() => navigate('/tickets/new')} className="apollo-card p-4 sm:p-5 flex items-center gap-3 hover:border-orange-500/30 transition-colors text-left min-w-0" style={{ minHeight: '64px' }}>
            <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(247,127,0,0.15)' }}>
              <Plus size={20} style={{ color: '#F77F00' }} />
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-white text-sm truncate">{t('create_ticket', lang)}</p>
              <p className="text-xs truncate" style={{ color: '#6B7280' }}>{lang === 'fr' ? 'Nouveau ticket' : 'New ticket'}</p>
            </div>
          </button>
          <button data-testid="quick-kb" onClick={() => navigate('/knowledge-base')} className="apollo-card p-4 sm:p-5 flex items-center gap-3 hover:border-orange-500/30 transition-colors text-left min-w-0" style={{ minHeight: '64px' }}>
            <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(34,197,94,0.15)' }}>
              <BookOpen size={20} style={{ color: '#22C55E' }} />
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-white text-sm truncate">{t('knowledge_base', lang)}</p>
              <p className="text-xs truncate" style={{ color: '#6B7280' }}>{lang === 'fr' ? 'Articles et guides' : 'Articles & guides'}</p>
            </div>
          </button>
          <button data-testid="quick-hashtags" onClick={() => navigate('/hashtags')} className="apollo-card p-4 sm:p-5 flex items-center gap-3 hover:border-orange-500/30 transition-colors text-left min-w-0" style={{ minHeight: '64px' }}>
            <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(168,85,247,0.15)' }}>
              <Hash size={20} style={{ color: '#A855F7' }} />
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-white text-sm truncate">{t('browse_hashtags', lang)}</p>
              <p className="text-xs truncate" style={{ color: '#6B7280' }}>{lang === 'fr' ? 'Explorer les tags' : 'Browse tags'}</p>
            </div>
          </button>
        </div>

        {/* Recent Tickets */}
        <div className="flex items-center justify-between gap-2 mb-4 min-w-0">
          <h2 className="text-base sm:text-lg font-semibold text-white truncate">{lang === 'fr' ? 'Tickets récents' : 'Recent Tickets'}</h2>
          <button onClick={() => navigate('/tickets')} className="flex items-center gap-1 text-sm whitespace-nowrap flex-shrink-0" style={{ color: '#F77F00' }}>
            {lang === 'fr' ? 'Voir tout' : 'View all'} <ArrowRight size={14} />
          </button>
        </div>

        <div className="space-y-2">
          {recentTickets.map(ticket => (
            <div key={ticket._id} data-testid={`home-ticket-${ticket._id}`}
              onClick={() => navigate(`/tickets/${ticket._id}`)}
              className="apollo-card p-4 flex items-center gap-4 cursor-pointer">
              <div className="w-10 h-10 rounded-full flex-shrink-0 flex items-center justify-center" style={{ background: 'rgba(247,127,0,0.15)' }}>
                <span style={{ color: '#F77F00', fontWeight: 600, fontSize: 14 }}>{ticket.created_by_name?.charAt(0)}</span>
              </div>
              <div className="flex-1 min-w-0">
                <span className="font-semibold text-white text-sm truncate block">{lang === 'en' ? (ticket.title_en || ticket.title_fr) : (ticket.title_fr || ticket.title_en)}</span>
                <span className="text-xs" style={{ color: '#6B7280' }}>{ticket.created_by_name} - {new Date(ticket.created_at).toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-US')}</span>
              </div>
              <StatusBadge status={ticket.status} />
            </div>
          ))}
        </div>
      </div>
    </Layout>
  );
}
